import { UserIdentityValidationResultOutputDto } from '../../../dto';
import { KycIdentityOriginId, UserIdentityStatus } from '../../../enums';
import { UserIdentity } from '../../../models';
import { formatDateToInputValue } from '../../../utils';
import { useDebounce } from '../../useDebounce';
import { useUserIdentityValidateByOriginId } from '../useUserIdentityValidateByOriginId';
import { useUserOriginId } from '../useUserOriginId';
import { UseUserIdentityLookUpHanlderProps } from './props';
import { useEffect } from 'react';

/**
 * Custom hook to handle user identity lookup.
 *
 * @param {UseUserIdentityLookUpHanlderProps} props - The properties for the hook.
 * @param {object} props.methods - Methods from react-hook-form.
 * @param {boolean} props.readonly - Flag to determine if the form is read-only.
 * @param {boolean} props.isSkeleton - Flag to determine if the skeleton view should be shown.
 * @param {string} props.externalOriginId - External origin ID for the user identity.
 *
 * @returns {object} - Returns an object containing the collapse state, validity of the lookup, and the query result.
 *
 * Example usage:
 * const { collapseIn, isValidLookup, useUserIdentityValidateByOriginIdQuery } = useUserIdentityLookUpHandler({
 *   methods: formMethods,
 *   readonly: false,
 *   isSkeleton: false,
 *   externalOriginId: '12345',
 * });
 */
export function useUserIdentityLookUpHandler({
  methods,
  readonly,
  isSkeleton,
  externalOriginId,
}: UseUserIdentityLookUpHanlderProps) {
  // Get the origin ID, either from the hook or external input
  const originId = useUserOriginId() ?? externalOriginId;
  // Destructure methods from react-hook-form
  const { watch, setValue } = methods;

  // Watch for changes in 'identifier', 'name', and 'birthDate' fields
  const identifier: string = watch('identity.identifier');
  const name: string = watch('identity.name');
  const birthDate: string = watch('identity.birthDate');

  // Debounce the watched values to prevent excessive updates
  const debouncedIdentifier = useDebounce(identifier);
  const debouncedName = useDebounce(name);
  const debouncedBirthDate = useDebounce(birthDate);

  // Prepare the payload for validation
  const payload = {
    identifier: debouncedIdentifier,
    name: name ? debouncedName : undefined,
    birthDate: birthDate ? String(debouncedBirthDate) : undefined,
    originId: originId?.toString(),
  };

  // Use the validation hook to validate the user identity by origin ID
  const useUserIdentityValidateByOriginIdQuery =
    useUserIdentityValidateByOriginId({
      //@ts-ignore
      originId: originId?.toString(),
      payload,
    });
  const { data, error } = useUserIdentityValidateByOriginIdQuery;

  /**
   * Function to set the identity ID based on the validation data.
   * It updates the form values if the data is valid and the origin ID is Brazil.
   */
  function handleSetIdentityId() {
    if (!data || originId != KycIdentityOriginId.Brazil) {
      return;
    }
    setValue('identity', {
      identifier: data?.maskedIdentifier,
      birthDate: formatDateToInputValue(String(data?.birthDate ?? new Date())),
      name: data?.name,
      originId: originId?.toString(),
      //@ts-ignore
      genderId: data?.genderId,
    });
    setValue('identityId', (data as unknown as UserIdentity)?.id.toString(), {
      shouldDirty: true,
    });
  }

  // Effect to handle setting the identity ID when data changes
  useEffect(handleSetIdentityId, [data, setValue, originId]);

  // Determine if the lookup is valid based on the origin ID and validation data
  const isValidLookup: boolean =
    originId?.toString() == KycIdentityOriginId.Brazil?.toString()
      ? (data as unknown as UserIdentity)?.status === UserIdentityStatus.Valid
      : (data as unknown as UserIdentityValidationResultOutputDto)?.valid;

  // Determine if the section should collapse based on various conditions
  const collapseIn =
    originId?.toString() == KycIdentityOriginId.Brazil?.toString()
      ? (isValidLookup && !error) || readonly || isSkeleton
      : true;

  // Return the state and query result
  return {
    collapseIn,
    isValidLookup,
    useUserIdentityValidateByOriginIdQuery,
  };
}

export type { UseUserIdentityLookUpHanlderProps };
